import { LogisticsProjectLookupComponent } from '../../../../logistics-projects/logistics-project-lookup.component';
import { ExpensePaymentDraftEditorComponent } from './expense-payment-draft-editor.component';
import { isEditableFinancialDraft } from '../../../shared/accounting-draft-eligibility';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountsService } from '../../../accounts.service';
import { APIListWrapper } from '../../../../models/common/api-list-wrapper.model';
import { BankAccountService } from "../../../bank-account/bank-account.service";
import { BankAccount } from "../../../bank-account/bank-account.model";
import { Party } from "../../../../party/party.model";
import { PartyService } from '../../../../party/party.service';
import { PartyType } from '../../../../party/party-type.enum';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BankAccountEntryComponent } from '../../../bank-account/bank-account-entry/bank-account-entry.component';
import { Voucher } from '../voucher.model';
import { Bill } from '../../generated-bills/bill.model';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenubarModule } from 'primeng/menubar';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PermissionService } from '../../../../security/permission.service';
import { app_permissions } from '../../../../security/app-permissions';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { LogisticsProjectsService } from '../../../../logistics-projects/logistics-projects.service';
import { CrmClient, CrmClientCommunication } from '../../../../crm/crm.model';
import { CRM_PAYABLE_ROLES, CrmPartyContextService } from '../../../../shared/crm-client-contact-picker/crm-party-context.service';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../../shared/page-layout/page-layout.models';
import { PageFormSectionComponent } from '../../../../shared/page-layout/page-form-section.component';
import { PageDataPanelComponent } from '../../../../shared/page-layout/page-data-panel.component';
import { CommercialPartyPickerComponent } from '../../../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../../../shared/commercial-party-picker/commercial-party.model';
import { CrmQuickCreateResult } from '../../../../shared/crm-quick-create/crm-quick-create-dialog.component';
import { ChartOfAccounts } from '../../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../../chart-of-accounts/chart-of-accounts.service';

// Interface for outstanding references (Bills/Orders)
interface OutstandingReference {
    id: number;
    type: string;
    name: string;
    date: Date;
    grandTotal: number;
    outstandingAmount: number;
    allocatedAmount: number;
    selected: boolean;
    logisticsProjectId?: number;
    currencyCode?: string;
}

@Component({
    selector: 'app-voucher-entry',
    templateUrl: './voucher-entry.component.html',
    styleUrls: ['./voucher-entry.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,ExpensePaymentDraftEditorComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        PanelModule,
        MenubarModule,
        ButtonModule,
        InputTextModule,
        InputNumberModule,
        DividerModule,
        CardModule,
        TableModule,
        TagModule,
        DialogModule,
        CheckboxModule,
        SelectModule,
        DatePickerModule,
        ToastModule,
        ProgressBarModule,
        BreadcrumbModule,
        FieldsetModule,
        InputGroupModule,
        TextareaModule,
        TooltipModule,
        DynamicDialogModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageFormSectionComponent,
        PageDataPanelComponent,
        CommercialPartyPickerComponent
    ],
    providers: [
        MessageService,
        AccountsService,
        SessionStorageService,
        BankAccountService,
        DialogService
    ]
})
export class VoucherEntryComponent implements OnInit, OnDestroy {

    readonly payablePartyRoles = CRM_PAYABLE_ROLES;
    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };

    // --- Class Properties ---
    isLoading: boolean = false;
    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    voucherEntryForm: FormGroup;
    editMode: boolean = false;
    voucherId: number | null = null;
    loadedVoucher: Voucher | null = null;

    // Dropdown options
    modeOfPaymentOptions: string[] = ['Cash', 'Bank Transfer', 'Cheque'];
    companyBankAccounts: BankAccount[] = [];
    chartAccounts: ChartOfAccounts[] = [];
    ledgerDefaults: {cashAccountId: number | null; receivableAccountId: number | null; payableAccountId: number | null} | null = null;
    crmClients: CrmClient[] = [];
    parties: Party[] = [];
    crmContacts: CrmClientCommunication[] = [];
    partyBankAccounts: BankAccount[] = [];

    // Allocation properties
    outstandingReferences: OutstandingReference[] = []; // Corrected type
    totalAllocatedAmount: number = 0;
    unallocatedAmount: number = 0;
    differenceAmount: number = 0;

    canSave = false;

    private dialogRef: DynamicDialogRef | undefined;

    get pageTitle(): string {
        return this.editMode ? 'Edit Voucher Entry' : 'New Voucher Entry';
    }

    get requiresBankAccount(): boolean {
        return this.voucherEntryForm.get('modeOfPayment')?.value !== 'Cash';
    }

    get selectedPartyName(): string {
        return this.voucherEntryForm.get('crmClient')?.value?.companyName || 'No vendor selected';
    }

    get selectedCurrencyCode(): string {
        return this.voucherEntryForm.get('currencyCode')?.value || 'SAR';
    }

    get selectedReferenceCount(): number {
        return this.outstandingReferences.filter(ref => ref.selected).length;
    }

    get metrics(): PageMetric[] {
        return [
            {
                label: 'Voucher amount',
                value: this.formatMoney(this.voucherEntryForm.get('paidAmount')?.value),
                hint: this.voucherEntryForm.get('modeOfPayment')?.value || 'Payment mode pending',
                tone: 'info'
            },
            {
                label: 'Allocated',
                value: this.formatMoney(this.totalAllocatedAmount),
                hint: `${this.selectedReferenceCount} bills selected`,
                tone: this.totalAllocatedAmount > 0 ? 'success' : 'default'
            },
            {
                label: 'Unallocated',
                value: this.formatMoney(this.unallocatedAmount),
                hint: 'Remaining voucher balance',
                tone: this.unallocatedAmount > 0 ? 'warn' : 'success'
            },
            {
                label: 'Outstanding refs',
                value: this.outstandingReferences.length,
                hint: this.selectedPartyName
            }
        ];
    }

    constructor(
        private fb: FormBuilder,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService,
        private accountingService: AccountsService,
        private chartOfAccountsService: ChartOfAccountsService,
        private bankAccountService: BankAccountService,
        private route: ActivatedRoute,
        private router: Router,
        private dialogService: DialogService,
        private permissionService: PermissionService,
        private logisticsProjectsService: LogisticsProjectsService,
        private crmPartyContext: CrmPartyContextService,
        private partyService: PartyService
    ) {
        // Form initialization remains the same
        this.voucherEntryForm = this.fb.group({
            postingDate: [new Date(), Validators.required],
            modeOfPayment: ['Cash', Validators.required],
            cashAccountId: [null],
            counterpartAccountId: [null],
            companyBankAccount: [null],
            paymentTransactionNumber: [''],
            party: [null, Validators.required],
            crmClient: [null],
            crmClientContact: [null],
            partyBankAccount: [null],
            paidAmount: [null, [Validators.required, Validators.min(0.01)]],
            logisticsProjectId: [Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null],
            currencyCode: [{ value: 'SAR', disabled: true }],
            remarks: [''],
        });
    }

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Accounts' }, { label: 'Vouchers' }, { label: 'New Voucher' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.route.params.subscribe(params => {
            if (params['id']) {
                this.voucherId = +params['id'];
                this.editMode = true;
                this.breadcrumbItems[2] = { label: `Edit Voucher` };
            }
        });

        if (this.editMode) {
            this.canSave = this.permissionService.hasPermission(app_permissions.accounts.voucher.edit);
        } else {
            this.canSave = this.permissionService.hasPermission(app_permissions.accounts.voucher.create);
        }

        this.loadInitialData();
    }

    ngOnDestroy(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    loadInitialData(): void {
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id;
        const countryId = this.sessionStorageService.getCountryList()?.
            find(value => value.id == this.sessionStorageService.getCompany()?.countryId)?.id ?? 0;

        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company ID not found.' });
            this.isLoading = false;
            return;
        }

        forkJoin({
            companyBankAccounts: this.bankAccountService.getBankAccounts('Company', companyId, '', 0, 1000),
            crmClients: this.crmPartyContext.loadClients(companyId, CRM_PAYABLE_ROLES),
            parties: this.partyService.getParties(companyId, Object.values(PartyType), '', 0, 1000),
            chartAccounts: this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false).pipe(catchError(() => of([] as ChartOfAccounts[]))),
            ledgerDefaults: this.accountingService.getPaymentLedgerDefaults(companyId).pipe(catchError(() => of(null)))
        }).pipe(
            finalize(() => this.isLoading = false),
            catchError(error => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load initial data.' });
                console.error('Initial data load error:', error);
                return of({ companyBankAccounts: null, crmClients: [], parties: null, chartAccounts: [], ledgerDefaults: null });
            })
        ).subscribe(data => {
            this.companyBankAccounts = data.companyBankAccounts?.items ?? [];
            this.crmClients = data.crmClients ?? [];
            this.parties = data.parties?.content ?? [];
            this.chartAccounts = data.chartAccounts ?? [];
            this.ledgerDefaults = data.ledgerDefaults;
            if (this.voucherId) {
                this.loadVoucherData(this.voucherId);
            }
        });
    }

    loadVoucherData(id: number): void {
        this.isLoading = true;
        this.accountingService.getVoucherById(id).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: (voucher) => {
                this.loadedVoucher = voucher;
                if (!isEditableFinancialDraft(voucher, this.sessionStorageService.getCompany()?.id)) {
                    this.canSave = false;
                    this.voucherEntryForm.disable();
                    this.messageService.add({ severity: 'error', summary: 'Draft unavailable', detail: 'Only unposted Draft documents for the selected company can be edited.' });
                    return;
                }
                if (voucher.sourceType === 'EXPENSE_PAYMENT') return;
                const companyBankAccount = voucher.fromBankAccount
                    ? this.companyBankAccounts.find(account => account.id === voucher.fromBankAccount?.id) || voucher.fromBankAccount
                    : null;
                const partyBankAccount = voucher.toBankAccount || null;
                const crmClient = this.crmClients.find(client => client.id === voucher.crmClientId)
                    || this.crmClients.find(client => this.partyMatchesClient(voucher.mainParty, client))
                    || null;

                this.voucherEntryForm.patchValue({
                    remarks: voucher.manualReasonText || '',
                    postingDate: voucher.voucherDate ? new Date(voucher.voucherDate) : new Date(),
                    modeOfPayment: this.displayPaymentMode(voucher.paymentMode)
                        || voucher.personalPaymentMethod?.paymentMethod?.paymentMethodName
                        || voucher.personalPaymentMethod?.paymentCardName
                        || (voucher.fromBankAccount ? 'Bank Transfer' : 'Cash'),
                    cashAccountId: voucher.cashAccountId ?? null,
                    counterpartAccountId: voucher.counterpartAccountId ?? null,
                    paymentTransactionNumber: voucher.paymentTransactionNumber || '',
                    paidAmount: voucher.amount,
                    logisticsProjectId: voucher.logisticsProjectId || this.voucherEntryForm.get('logisticsProjectId')?.value,
                    party: voucher.mainParty || null,
                    crmClient,
                    companyBankAccount,
                    partyBankAccount,
                    currencyCode: voucher.country?.currencyCode || this.selectedCurrencyCode
                });
                this.commercialPartyReference = {
                    source: voucher.crmClientId ? 'CRM' : 'LEGACY', crmClientId: voucher.crmClientId,
                    crmAccountId: voucher.crmAccountId, crmContactId: voucher.crmClientContactId,
                    legacyCompanyId: voucher.mainParty?.id?.entityType === PartyType.Company ? voucher.mainParty.id.entityId : undefined,
                    legacyCustomerId: voucher.subParty?.id?.entityId
                        ?? (voucher.mainParty?.id?.entityType === PartyType.Customer ? voucher.mainParty.id.entityId : undefined)
                };
                if (crmClient) {
                    this.loadCrmContacts(crmClient, voucher.crmClientContactId);
                    this.loadPartyBankAccounts(this.crmPartyContext.partyFromClient(crmClient), partyBankAccount);
                } else if (voucher.mainParty) {
                    this.loadPartyBankAccounts(voucher.mainParty, partyBankAccount);
                }

                // --- Populate Allocations (UPDATED LOGIC) ---
                if (voucher.bills && voucher.bills.length > 0) {
                    this.outstandingReferences = voucher.bills.map((bill: Bill) => {
                        // The amount of *this specific voucher* that was allocated to the bill.
                        const allocatedForThisVoucher = bill.allocatedAmount
                            ?? bill.vouchers?.find(r => r.voucherId === voucher.voucherId)?.amount
                            ?? 0;

                        // The currently outstanding amount on the bill.
                        const currentOutstanding = (bill.totalAmount ?? 0) -
                            (bill.vouchers?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0);

                        // For display, show what was outstanding *before* this voucher was applied.
                        const outstandingBeforeThisVoucher = currentOutstanding + allocatedForThisVoucher;

                        return {
                            id: bill.billId!,
                            type: 'Purchase Bill',
                            name: bill.billNumber!,
                            date: new Date(bill.billDate!),
                            grandTotal: bill.totalAmount!,
                            outstandingAmount: outstandingBeforeThisVoucher,
                            allocatedAmount: allocatedForThisVoucher,
                            selected: true,
                            logisticsProjectId: bill.logisticsProjectId
                        };
                    });
                    this.calculateAllocationTotals();
                }
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load voucher data.' });
                console.error('Load voucher data error:', err);
            }
        });
    }

    onCrmClientPicked(client: CrmClient | null): void {
        this.voucherEntryForm.get('crmClient')?.setValue(client);
        this.crmContacts = [];
        this.partyBankAccounts = [];
        this.voucherEntryForm.get('crmClientContact')?.setValue(null);
        this.voucherEntryForm.get('partyBankAccount')?.setValue(null);
        this.outstandingReferences = [];
        this.calculateAllocationTotals();

        if (!client) return;
        this.voucherEntryForm.get('currencyCode')?.setValue(client.currencyCode || this.defaultCurrencyCode());
        this.loadCrmContacts(client, this.commercialPartyReference.crmContactId ?? undefined);
        const party = this.crmPartyContext.partyFromClient(client);
        if (!party) {
            this.messageService.add({ severity: 'warn', summary: 'CRM link required', detail: 'Selected CRM client is not linked to a vendor party.' });
            return;
        }
        this.voucherEntryForm.get('party')?.setValue(party, { emitEvent: false });
        this.loadPartyBankAccounts(party);
        this.getOutstandingBills();
    }

    get commercialPartyCompanyId(): number | undefined { return this.sessionStorageService.getCompany()?.id; }

    onCrmQuickCreated(result: CrmQuickCreateResult): void {
        if (result.mode === 'client') this.crmClients = [...this.crmClients, result.record];
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = reference;
        if (reference.source === 'CRM') {
            const client = this.crmClients.find(item => item.id === reference.crmClientId) || null;
            this.onCrmClientPicked(client);
            this.voucherEntryForm.patchValue({
                crmClient: client,
                crmClientContact: this.crmContacts.find(item => item.id === reference.crmContactId) || null
            });
            return;
        }
        const partyId = reference.legacyCustomerId ?? reference.legacyCompanyId;
        const party = this.parties.find(item => item.id.entityId === partyId) || null;
        this.voucherEntryForm.patchValue({ party, crmClient: null, crmClientContact: null });
        this.onPartyPicked(party);
    }

    onCrmContactPicked(contact: CrmClientCommunication | null): void {
        this.voucherEntryForm.get('crmClientContact')?.setValue(contact);
    }

    onPartyPicked(party: Party | null): void {
        this.partyBankAccounts = [];
        this.outstandingReferences = [];
        this.calculateAllocationTotals();
        this.voucherEntryForm.get('partyBankAccount')?.setValue(null);
        if (this.voucherEntryForm.get('crmClient')?.value) {
            this.voucherEntryForm.patchValue({ crmClient: null, crmClientContact: null }, { emitEvent: false });
            this.crmContacts = [];
        }
        if (party) {
            this.loadPartyBankAccounts(party);
            this.getOutstandingBills();
        }
    }

    private loadCrmContacts(client: CrmClient, selectedContactId?: number): void {
        this.crmPartyContext.loadContacts(client.id, this.sessionStorageService.getCompany()?.id).subscribe(contacts => {
            this.crmContacts = contacts;
            this.voucherEntryForm.get('crmClientContact')?.setValue(this.crmPartyContext.preferredContact(contacts, selectedContactId));
        });
    }

    private loadPartyBankAccounts(party: Party | null, selectedBankAccount?: BankAccount | null): void {
        if (!party) return;
        this.bankAccountService.getBankAccounts(party.id.entityType, party.id.entityId, '', 0, 1000).subscribe({
            next: (bankAccounts) => {
                this.partyBankAccounts = bankAccounts.items;
                this.voucherEntryForm.get('partyBankAccount')?.setValue(
                    selectedBankAccount
                        ? this.partyBankAccounts.find(account => account.id === selectedBankAccount.id) || selectedBankAccount
                        : null
                );
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load party bank accounts.' });
                console.error('Party bank accounts load error:', err);
            }
        });
    }

    getOutstandingBills(): void {
        const party = this.voucherEntryForm.get('party')?.value as Party | null;
        const companyId = this.sessionStorageService.getCompany()?.id;

        if (!party?.id || !companyId) {
            this.messageService.add({ severity: 'warn', summary: 'Selection Required', detail: 'Please select a party first.' });
            return;
        }

        this.isLoading = true;
        this.accountingService.getGeneratedBills(0, 500, companyId, undefined, undefined, undefined, undefined, undefined, {
            postingStatuses: ['POSTED'],
            paymentStatuses: ['UNPAID', 'PARTIALLY_PAID'],
            currencyCode: this.selectedCurrencyCode,
            partyId: party.id.entityId,
            partyType: party.id.entityType
        }).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: (bills: APIListWrapper<Bill>) => {
                this.outstandingReferences = (bills.items ?? []).map(bill => {
                    const outstanding = bill.openAmount ?? ((bill.totalAmount ?? 0) - (bill.vouchers?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0));
                    return {
                        id: bill.billId!,
                        type: 'Purchase Bill',
                        name: bill.billNumber!,
                        date: new Date(bill.billDate!),
                        grandTotal: bill.totalAmount!,
                        outstandingAmount: outstanding,
                        allocatedAmount: 0,
                        selected: false,
                        logisticsProjectId: bill.logisticsProjectId,
                        currencyCode: bill.country?.currencyCode || this.selectedCurrencyCode
                    };
                }).filter(reference => reference.outstandingAmount > 0);
                this.calculateAllocationTotals();
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load outstanding bills.' });
            }
        });
    }

    onReferenceSelect(ref: OutstandingReference): void {
        if (ref.selected) {
            const paidAmount = this.voucherEntryForm.get('paidAmount')?.value || 0;
            const remainingUnallocated = paidAmount - this.totalAllocatedAmount;
            ref.allocatedAmount = Math.min(ref.outstandingAmount, remainingUnallocated > 0 ? remainingUnallocated : 0);
            if (!this.voucherEntryForm.get('logisticsProjectId')?.value && ref.logisticsProjectId) {
                this.voucherEntryForm.get('logisticsProjectId')?.setValue(ref.logisticsProjectId);
            }
        } else {
            ref.allocatedAmount = 0;
        }
        this.calculateAllocationTotals();
    }

    onAllocationChange(ref: OutstandingReference): void {
        if (ref.allocatedAmount > ref.outstandingAmount) {
            ref.allocatedAmount = ref.outstandingAmount;
        }
        this.calculateAllocationTotals();
    }

    calculateAllocationTotals(): void {
        this.totalAllocatedAmount = this.outstandingReferences
            .filter(ref => ref.selected)
            .reduce((sum, ref) => sum + (ref.allocatedAmount || 0), 0);

        const paidAmount = this.voucherEntryForm.get('paidAmount')?.value || 0;
        this.unallocatedAmount = paidAmount - this.totalAllocatedAmount;
        this.differenceAmount = this.unallocatedAmount;
    }

    formatMoney(amount: number | null | undefined, currencyCode: string = this.selectedCurrencyCode): string {
        return `${currencyCode} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    /**
     * Handles changes to the Mode of Payment dropdown.
     * You can add logic here, e.g., to make the bank account field
     * required only for 'Bank Transfer' or 'Cheque'.
     */
    onModeOfPaymentChange(): void {
        const mode = this.voucherEntryForm.get('modeOfPayment')?.value;
        const bankAccountControl = this.voucherEntryForm.get('companyBankAccount');

        if (mode === 'Cash') {
            bankAccountControl?.setValue(null);
            bankAccountControl?.clearValidators();
        } else {
            this.voucherEntryForm.get('cashAccountId')?.setValue(null);
            bankAccountControl?.setValidators([Validators.required]);
        }
        bankAccountControl?.updateValueAndValidity();
    }

    get cashAccounts(): ChartOfAccounts[] {
        return this.eligibleAccounts('CASH');
    }

    get eligibleBankAccounts(): BankAccount[] {
        return this.companyBankAccounts.filter(bank => bank.account?.accountId
            && bank.owningCompanyId === this.sessionStorageService.getCompany()?.id
            && bank.account.owningCompanyId === bank.owningCompanyId
            && bank.account.isActive && !bank.account.deprecated
            && (!bank.account.currencyCode || bank.account.currencyCode.toUpperCase() === this.selectedCurrencyCode.toUpperCase())
            && bank.currencyCode?.toUpperCase() === this.selectedCurrencyCode.toUpperCase());
    }

    get counterpartAccounts(): ChartOfAccounts[] {
        return this.eligibleAccounts('AP');
    }

    private eligibleAccounts(type: string): ChartOfAccounts[] {
        return this.chartAccounts.filter(account => account.isActive && !account.deprecated
            && account.owningCompanyId === this.sessionStorageService.getCompany()?.id
            && (!account.currencyCode || account.currencyCode.toUpperCase() === this.selectedCurrencyCode.toUpperCase())
            && account.accountType?.code?.toUpperCase() === type)
            .map(account => ({ ...account, accountName: `${account.accountCode} — ${account.accountName}` }));
    }

    accountLabel(accountId: number | null | undefined): string {
        const account = this.chartAccounts.find(item => item.accountId === accountId);
        return account ? `${account.accountCode} — ${account.accountName}` : 'Unavailable';
    }

    private displayPaymentMode(mode?: string): string | null {
        return mode === 'CASH' ? 'Cash' : mode === 'BANK_TRANSFER' ? 'Bank Transfer'
            : mode === 'CHEQUE' ? 'Cheque' : null;
    }

    /**
     * Handles changes to the Paid Amount input.
     * This is a good place to recalculate allocations if needed.
     */
    onPaidAmountChange(): void {
        this.calculateAllocationTotals();
    }

    saveVoucherEntry(): void {
        if (!this.canSave || (this.editMode && !isEditableFinancialDraft(this.loadedVoucher, this.sessionStorageService.getCompany()?.id))) return;
        this.isLoading = true;
        if (this.voucherEntryForm.invalid) {
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please fill all required fields.' });
            this.voucherEntryForm.markAllAsTouched();
            this.isLoading = false;
            return;
        }
        this.calculateAllocationTotals();
        if (this.totalAllocatedAmount > (this.voucherEntryForm.get('paidAmount')?.value || 0)) {
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Allocated amount cannot exceed voucher amount.' });
            this.isLoading = false;
            return;
        }

        const party = this.voucherEntryForm.get('party')?.value as Party | null;
        const crmClient = this.voucherEntryForm.get('crmClient')?.value as CrmClient | null;
        const crmContact = this.voucherEntryForm.get('crmClientContact')?.value as CrmClientCommunication | null;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!party?.id) {
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Select the vendor or other paying party before saving.' });
            this.isLoading = false;
            return;
        }

        let voucher = {
            paymentMode: this.voucherEntryForm.get('modeOfPayment')?.value === 'Cash' ? 'CASH'
                : this.voucherEntryForm.get('modeOfPayment')?.value === 'Cheque' ? 'CHEQUE' : 'BANK_TRANSFER',
            cashAccountId: this.voucherEntryForm.get('modeOfPayment')?.value === 'Cash'
                ? this.voucherEntryForm.get('cashAccountId')?.value || undefined : undefined,
            counterpartAccountId: this.voucherEntryForm.get('counterpartAccountId')?.value || undefined,
            voucherId: this.voucherId || undefined,
            voucherNumber: this.loadedVoucher?.voucherNumber,
            voucherDate: this.voucherEntryForm.get('postingDate')?.value || new Date(),
            isDigitalPayment: this.voucherEntryForm.get('modeOfPayment')?.value === 'Bank Transfer',
            amount: this.voucherEntryForm.get('paidAmount')?.value || 0,
            logisticsProjectId: this.voucherEntryForm.get('logisticsProjectId')?.value || undefined,
            country: this.sessionStorageService.getCountryList()!
                .find(value => value.id == this.sessionStorageService.getCompany()?.countryId),
            mainParty: party,
            subParty: party,
            crmClientId: this.commercialPartyReference.crmClientId ?? crmClient?.id,
            crmAccountId: this.commercialPartyReference.crmAccountId,
            crmClientContactId: this.commercialPartyReference.crmContactId ?? crmContact?.id,
            owningCompany: this.sessionStorageService.getCompany(),
            fromBankAccount: this.voucherEntryForm.get('modeOfPayment')?.value === 'Cash'
                ? undefined : this.voucherEntryForm.get('companyBankAccount')?.value,
            toBankAccount: this.voucherEntryForm.get('modeOfPayment')?.value === 'Cash'
                ? undefined : this.voucherEntryForm.get('partyBankAccount')?.value,
            paymentTransactionNumber: this.voucherEntryForm.get('paymentTransactionNumber')?.value || undefined,
            documentStatus: this.loadedVoucher?.documentStatus,
            workflowStatus: this.loadedVoucher?.workflowStatus,
            postingStatus: this.loadedVoucher?.postingStatus,
            sourceType: this.loadedVoucher?.sourceType || 'MANUAL',
            sourceReference: this.loadedVoucher?.sourceReference || this.voucherEntryForm.get('paymentTransactionNumber')?.value || undefined,
            manualReasonCode: this.loadedVoucher?.manualReasonCode || 'ON_ACCOUNT_PAYMENT',
            manualReasonText: this.voucherEntryForm.get('remarks')?.value || this.loadedVoucher?.manualReasonText || 'Manual vendor payment entered from portal.',
            createdFromModule: this.loadedVoucher?.createdFromModule || 'ACCOUNTS_VOUCHER_ENTRY',
            requestedBy: this.loadedVoucher?.requestedBy || this.sessionStorageService.getCustomer()?.id,
            createdAt: this.loadedVoucher?.createdAt || new Date(),
            updatedAt: new Date(),
        } as Voucher;
        const allocations = this.outstandingReferences
            .filter(ref => ref.selected && ref.allocatedAmount > 0)
            .map(ref => ({
                billId: ref.id,
                allocatedAmount: ref.allocatedAmount
            }));
        const saveRequest = this.voucherId
            ? this.accountingService.updateVoucher(this.voucherId, voucher)
            : allocations.length > 0
            ? this.accountingService.createVoucherWithAllocations(voucher, { allocations })
            : this.accountingService.createVoucher(voucher);
        saveRequest.subscribe({
            next: created => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Voucher saved successfully.' });
                this.linkProjectRequirementFulfillment(created).add(() => {
                    this.isLoading = false;
                    this.router.navigate(['/accounts/enterprise/payables/generated-vouchers']);
                });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Failed to save voucher.' + err.error.message });
                console.error('Save voucher error:', err);
                this.isLoading = false;
            }
        });
    }

    private linkProjectRequirementFulfillment(created: Voucher) {
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || 0);
        const requirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
        const owningCompanyId = Number(this.sessionStorageService.getCompany()?.id || 0);
        const targetResourceId = Number(created.voucherId || this.voucherId || 0);
        if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
            return { add: (callback: () => void) => callback() };
        }
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: this.route.snapshot.queryParamMap.get('targetResourceType') || 'VENDOR_VOUCHER',
            targetResourceId,
            sourceModule: 'ACCOUNTS_PAYABLE',
            sourceReference: created.voucherNumber || `Voucher #${targetResourceId}`,
            fulfilledQuantity: Number(this.route.snapshot.queryParamMap.get('quantity') || 1),
            fulfillmentStatus: 'LINKED',
            status: 'ACTIVE'
        }).subscribe({
            error: error => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: error?.error?.message || 'Voucher was saved, but requirement fulfillment was not linked.'
            })
        });
    }

    cancel(): void {
        this.router.navigate(['/accounts/enterprise/payables/generated-vouchers']);
    }

    showNewBankAccountDialog(): void {
        const party = this.voucherEntryForm.get('party')?.value as Party | null;
        if (!party) {
            this.messageService.add({ severity: 'warn', summary: 'Party Not Selected', detail: 'Please select a party to add a bank account.' });
            return;
        }

        this.dialogRef = this.dialogService.open(BankAccountEntryComponent, {
            header: 'Create New Bank Account',
            width: '60%',
            contentStyle: { "max-height": "500px", "overflow": "auto" },
            baseZIndex: 10000,
            modal: true,
            data: {
                ownerType: party.id.entityType,
                ownerId: party.id.entityId,
                ownerName: party.entityName,
                isDialog: true
            }
        });

        this.dialogRef.onClose.subscribe((newAccount: BankAccount) => {
            if (newAccount) {
                this.partyBankAccounts = [...this.partyBankAccounts, newAccount];
                this.voucherEntryForm.get('partyBankAccount')?.setValue(newAccount);
                this.messageService.add({ severity: 'success', summary: 'Bank Account Added', detail: 'New bank account has been created and selected.' });
            }
        });
    }

    private partyMatchesClient(party: Party | undefined, client: CrmClient): boolean {
        if (!party) return false;
        return (party.id.entityType === 'Company' && client.linkedCompanyId === party.id.entityId)
            || (party.id.entityType === 'Customer' && client.linkedCustomerId === party.id.entityId)
            || (party.id.entityType === 'TransportationAgency' && client.linkedTransportationAgencyId === party.id.entityId);
    }

    private defaultCurrencyCode(): string {
        return this.sessionStorageService.getCountryList()
            ?.find(value => value.id === this.sessionStorageService.getCompany()?.countryId)
            ?.currencyCode || 'SAR';
    }
}
